import { Box, Divider, Stack, styled } from "@mui/material"
import {
  type PropsWithChildren,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import {
  FinancialSummaryTitleCard,
  FinancialSummaryTitleCardMini,
} from "./FinancialSummaryTitleCard"

type FinancialSummaryProps = PropsWithChildren<{ title: ReactNode }>

export const FinancialSummary = ({
  isCompact,
  ...props
}: FinancialSummaryProps & { isCompact: boolean }) => {
  return isCompact ? (
    <FinancialSummaryCompact {...props} />
  ) : (
    <FinancialSummaryFull {...props} />
  )
}

const FinancialSummaryFull = ({ title, children }: FinancialSummaryProps) => {
  return (
    <Box py={0.75}>
      <Container>
        <SummaryContainer>
          <FinancialSummaryTitleCard>{title}</FinancialSummaryTitleCard>
        </SummaryContainer>
        <ChildrenContainer>{children}</ChildrenContainer>
      </Container>
    </Box>
  )
}

const FinancialSummaryCompact = ({ title, children }: FinancialSummaryProps) => {
  const titleRef = useRef<HTMLDivElement>(null)
  const [titleHeight, setTitleHeight] = useState(0)

  useLayoutEffect(() => {
    const el = titleRef.current
    if (!el) return
    const height = el.getBoundingClientRect().height
    setTitleHeight(height)
  }, [])

  return (
    <Box pt={`${titleHeight / 2}px`}>
      <Container>
        <SummaryContainerMini ref={titleRef}>
          <FinancialSummaryTitleCardMini>{title}</FinancialSummaryTitleCardMini>
        </SummaryContainerMini>
        <ChildrenContainerMini>{children}</ChildrenContainerMini>
      </Container>
    </Box>
  )
}

const Container = styled(Stack)(({ theme: { palette, spacing } }) => ({
  position: "relative",
  flexDirection: "row",
  border: `1px solid ${palette.divider}`,
  borderRadius: spacing(0.75),
  cornerShape: "squircle",
  paddingInline: `clamp(${spacing(2.5)}, 1.5vw, ${spacing(4)})`,
  backgroundColor: palette.common.white,
}))

const SummaryContainer = styled(Stack)(({ theme: { spacing } }) => ({
  marginBlock: spacing(-0.75),
}))

const SummaryContainerMini = styled(Stack)(({ theme: { spacing } }) => ({
  position: "absolute",
  height: "fit-content",
  top: 0,
  transform: "translateY(-50%)",
  marginBlock: 0,
}))

const ChildrenContainer = styled(Stack)(({ theme: { spacing } }) => ({
  flexDirection: "row",
  flex: 1,
  gap: `clamp(${spacing(2.5)}, 1.5vw, ${spacing(4)})`,
  paddingBlock: spacing(2.5),
  paddingLeft: `clamp(${spacing(2.5)}, 1.5vw, ${spacing(4)})`,
}))
ChildrenContainer.defaultProps = {
  divider: <Divider flexItem orientation="vertical" />,
}
const ChildrenContainerMini = styled(ChildrenContainer)(
  ({ theme: { spacing } }) => ({
    paddingBlock: `${spacing(4.5)} ${spacing(3.5)}`,
    paddingLeft: 0,
  }),
)
